/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { applyPhotoFilter, blurCanvas, supportsCanvasFilters } from './image-editor-filters'
import { getOrientedSize } from './image-editor-geometry'
import {
  type CropRect,
  type EditorCover,
  type EditorSticker,
  getCoverBlurPx,
  getCoverPixelCellPx,
  getPhotoFilterCss,
  JPEG_QUALITY,
  ORIENTED_EXPORT_MAX_DIMENSION,
  type PhotoFilter,
  type StickerKind,
} from './image-editor-types'

const isCrossOriginUrl = (src: string) => src.startsWith('http://') || src.startsWith('https://')

export const loadHtmlImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image()
    // Blob and data URLs are same-origin. Forcing CORS on them makes some browsers
    // fail the load even though the bytes are local.
    if (isCrossOriginUrl(src)) {
      image.crossOrigin = 'anonymous'
    }
    image.addEventListener('load', () => resolve(image), { once: true })
    image.addEventListener('error', () => reject(new Error('Failed to load image')), { once: true })
    image.src = src
  })

export const canvasToBlob = (canvas: HTMLCanvasElement, type = 'image/jpeg', quality = JPEG_QUALITY) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
          return
        }
        reject(new Error('Failed to export image'))
      },
      type,
      quality
    )
  })

const requireContext = (canvas: HTMLCanvasElement, settings?: CanvasRenderingContext2DSettings) => {
  const context = canvas.getContext('2d', settings)
  if (!context) {
    throw new Error('Could not create canvas context')
  }
  return context
}

const fitWithinMaxDimension = (width: number, height: number, maxDimension: number) => {
  const longest = Math.max(width, height)
  if (longest <= maxDimension) {
    return { width, height }
  }
  const scale = maxDimension / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export const applyOrientationTransform = (
  context: Pick<CanvasRenderingContext2D, 'rotate' | 'scale'>,
  rotation: number,
  flipX: boolean
) => {
  // View-space horizontal flip: scale the current view, then rotate the source into it.
  // Canvas applies the last transform first, so scale must be specified before rotate.
  if (flipX) {
    context.scale(-1, 1)
  }
  context.rotate((rotation * Math.PI) / 180)
}

export const renderOrientedImage = (
  source: HTMLImageElement,
  rotation: number,
  flipX: boolean,
  maxDimension?: number
) => {
  const sourceWidth = source.naturalWidth || source.width
  const sourceHeight = source.naturalHeight || source.height
  const oriented = getOrientedSize(sourceWidth, sourceHeight, rotation)
  const fitted = maxDimension ? fitWithinMaxDimension(oriented.width, oriented.height, maxDimension) : oriented
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(fitted.width))
  canvas.height = Math.max(1, Math.round(fitted.height))
  const context = requireContext(canvas)
  const sourceScale = rotation % 180 === 0 ? canvas.width / sourceWidth : canvas.width / sourceHeight
  const drawWidth = sourceWidth * sourceScale
  const drawHeight = sourceHeight * sourceScale

  context.translate(canvas.width / 2, canvas.height / 2)
  applyOrientationTransform(context, rotation, flipX)
  context.drawImage(source, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight)
  return canvas
}

const BLUR_SAMPLE_SIGMAS = 3

const pathCoverOval = (context: CanvasRenderingContext2D, width: number, height: number) => {
  context.beginPath()
  context.ellipse(0, 0, width / 2, height / 2, 0, 0, Math.PI * 2)
}

const drawBlurredImage = (
  context: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  blurPx: number,
  canvasWidth: number,
  canvasHeight: number,
  centerX: number,
  centerY: number,
  coverWidth: number,
  coverHeight: number,
  rotation: number
) => {
  if (supportsCanvasFilters()) {
    context.filter = `blur(${blurPx}px)`
    context.drawImage(source, 0, 0, canvasWidth, canvasHeight)
    context.filter = 'none'
    return
  }

  const radians = (rotation * Math.PI) / 180
  const absCos = Math.abs(Math.cos(radians))
  const absSin = Math.abs(Math.sin(radians))
  const boundsWidth = coverWidth * absCos + coverHeight * absSin
  const boundsHeight = coverWidth * absSin + coverHeight * absCos
  const pad = Math.ceil(blurPx * BLUR_SAMPLE_SIGMAS)
  const left = Math.max(0, Math.floor(centerX - boundsWidth / 2 - pad))
  const top = Math.max(0, Math.floor(centerY - boundsHeight / 2 - pad))
  const right = Math.min(canvasWidth, Math.ceil(centerX + boundsWidth / 2 + pad))
  const bottom = Math.min(canvasHeight, Math.ceil(centerY + boundsHeight / 2 + pad))
  const regionWidth = Math.max(1, right - left)
  const regionHeight = Math.max(1, bottom - top)
  const region = document.createElement('canvas')
  region.width = regionWidth
  region.height = regionHeight
  const regionContext = requireContext(region, { willReadFrequently: true })
  regionContext.drawImage(source, left, top, regionWidth, regionHeight, 0, 0, regionWidth, regionHeight)
  blurCanvas(region, blurPx)
  context.drawImage(region, left, top)
}

const drawCover = (
  context: CanvasRenderingContext2D,
  cover: EditorCover,
  source: HTMLCanvasElement,
  canvasWidth: number,
  canvasHeight: number
) => {
  const width = cover.width * canvasWidth
  const height = cover.height * canvasHeight
  const centerX = cover.x * canvasWidth + width / 2
  const centerY = cover.y * canvasHeight + height / 2

  context.save()
  context.translate(centerX, centerY)
  context.rotate((cover.rotation * Math.PI) / 180)

  if (cover.kind === 'emoji') {
    const fontSize = Math.min(width, height) * 0.9
    context.font = `${fontSize}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.fillText(cover.emoji, 0, 0)
    context.restore()
    return
  }

  pathCoverOval(context, width, height)
  context.clip()
  context.rotate((-cover.rotation * Math.PI) / 180)
  context.translate(-centerX, -centerY)

  if (cover.kind === 'blur') {
    drawBlurredImage(
      context,
      source,
      getCoverBlurPx(width, height),
      canvasWidth,
      canvasHeight,
      centerX,
      centerY,
      width,
      height,
      cover.rotation
    )
  } else {
    const cell = getCoverPixelCellPx(width, height)
    const small = document.createElement('canvas')
    small.width = Math.max(1, Math.round(canvasWidth / cell))
    small.height = Math.max(1, Math.round(canvasHeight / cell))
    const smallContext = requireContext(small)
    smallContext.drawImage(source, 0, 0, small.width, small.height)
    context.imageSmoothingEnabled = false
    context.drawImage(small, 0, 0, canvasWidth, canvasHeight)
    context.imageSmoothingEnabled = true
  }

  context.restore()
}

const drawSticker = (
  context: CanvasRenderingContext2D,
  sticker: EditorSticker,
  stickerImage: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
) => {
  const width = sticker.width * canvasWidth
  const height = sticker.height * canvasHeight
  const centerX = sticker.x * canvasWidth + width / 2
  const centerY = sticker.y * canvasHeight + height / 2

  context.save()
  context.translate(centerX, centerY)
  context.rotate((sticker.rotation * Math.PI) / 180)
  context.scale(sticker.flipX ? -1 : 1, 1)
  context.drawImage(stickerImage, -width / 2, -height / 2, width, height)
  context.restore()
}

export const exportEditedImage = async ({
  sourceUrl,
  rotation,
  flipX,
  crop,
  photoFilter,
  stickers,
  covers,
  stickerSources,
}: {
  sourceUrl: string
  rotation: number
  flipX: boolean
  crop: CropRect
  photoFilter: PhotoFilter
  stickers: EditorSticker[]
  covers: EditorCover[]
  stickerSources: Record<StickerKind, string>
}) => {
  const photo = await loadHtmlImage(sourceUrl)
  const oriented = renderOrientedImage(photo, rotation, flipX, ORIENTED_EXPORT_MAX_DIMENSION)
  const cropX = Math.round(crop.x * oriented.width)
  const cropY = Math.round(crop.y * oriented.height)
  const cropWidth = Math.max(1, Math.round(crop.width * oriented.width))
  const cropHeight = Math.max(1, Math.round(crop.height * oriented.height))

  const output = document.createElement('canvas')
  output.width = cropWidth
  output.height = cropHeight
  const context = requireContext(output, supportsCanvasFilters() ? undefined : { willReadFrequently: true })
  if (supportsCanvasFilters()) {
    context.filter = getPhotoFilterCss(photoFilter)
    context.drawImage(oriented, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
    context.filter = 'none'
  } else {
    context.drawImage(oriented, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
    applyPhotoFilter(context, photoFilter)
  }

  const photoLayer = document.createElement('canvas')
  photoLayer.width = cropWidth
  photoLayer.height = cropHeight
  requireContext(photoLayer).drawImage(output, 0, 0)

  for (const cover of covers) {
    drawCover(context, cover, photoLayer, cropWidth, cropHeight)
  }

  const stickerImages = new Map<StickerKind, HTMLImageElement>()
  for (const sticker of stickers) {
    let stickerImage = stickerImages.get(sticker.kind)
    if (!stickerImage) {
      stickerImage = await loadHtmlImage(stickerSources[sticker.kind])
      stickerImages.set(sticker.kind, stickerImage)
    }
    drawSticker(context, sticker, stickerImage, cropWidth, cropHeight)
  }

  return canvasToBlob(output)
}
